export type ProPlan = 'bb_pro_monthly' | 'bb_pro_annual' | 'bb_pro_wu_annual'

export type ProCheckoutPlan = ProPlan

export type ProCheckoutSession = {
  url: string
}

export type ProPromoCodeValidation = {
  valid: boolean
  name?: string
  percentOff?: number
  amountOff?: number
  trialDays?: number
  trialEndsAt?: string
}

export type ProSubscriptionRail = 'stripe' | 'iap'

export type ProSubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'paused'
  | 'past_due'
  | 'cancelled'

export type ProPlanPrice = {
  amount: number
  currency: string
  formatted: string
  interval: string | null
}

export type ProPlanCatalogEntry = {
  plan: ProPlan
  price: ProPlanPrice
}

export type ProPlanCatalog = {
  plans: ProPlanCatalogEntry[]
}

export function proPlanPrice(
  catalog: ProPlanCatalog | null,
  plan: ProPlan,
): ProPlanPrice | null {
  return catalog?.plans.find((entry) => entry.plan === plan)?.price ?? null
}

export function proPlanPriceLabel(price: ProPlanPrice | null): string {
  if (price === null) return ''
  if (price.interval === 'month') return `${price.formatted}/mo`
  if (price.interval === 'year') return `${price.formatted}/yr`
  return price.formatted
}

export function proAnnualSavingsLabel(
  monthly: ProPlanPrice | null,
  annual: ProPlanPrice | null,
): string {
  if (monthly === null || annual === null) return ''
  if (monthly.interval !== 'month' || annual.interval !== 'year') return ''
  const yearlyAtMonthlyRate = monthly.amount * 12
  if (yearlyAtMonthlyRate <= 0 || annual.amount >= yearlyAtMonthlyRate) return ''
  const percent = Math.round(((yearlyAtMonthlyRate - annual.amount) / yearlyAtMonthlyRate) * 100)
  return percent <= 0 ? '' : `Save ${percent}%`
}

export type ProSubscriptionSummary = {
  plan: ProPlan | null
  rail: ProSubscriptionRail | null
  status: ProSubscriptionStatus | null
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
  pastDueSince: string | null
  cancelAt: string | null
  pendingPlan: ProPlan | null
  pendingPlanEffectiveAt: string | null
  defaultCardBrand: string | null
  defaultCardLast4: string | null
  defaultCardExpMonth: number | null
  defaultCardExpYear: number | null
  price: ProPlanPrice | null
}

export type ProPlanChangeDirection = 'upgrade' | 'downgrade'

export type ProPlanChangePreview = {
  direction: ProPlanChangeDirection
  amountDueNow: number
  creditAmount: number
  currency: string
  prorationDate: number
  nextRenewalAt: string | null
  nextRenewalAmount: number | null
}

export type ProPlanChangeResult = {
  direction: ProPlanChangeDirection
  applied: boolean
  plan: ProPlan
  pendingPlan: ProPlan | null
  pendingPlanEffectiveAt: string | null
  cancelAtPeriodEnd: boolean
  currentPeriodEnd: string | null
  pendingUpdateExpiresAt: string | null
}
